"use client";

import { useState, useEffect, useRef } from "react";
import { MessageCircle, Send } from "lucide-react";

interface Message {
  content: string;
  type: "bot" | "user";
  id: number;
}

const steps = [
  {
    content: `The topics discussed below might be new to you as a retail investor, but they're part of the portfolio for almost every institutional investor, HNI, and wealthy Family Office.
  
  Most of them used these products for tax efficiency until 2023. After 2023, taxation is applicable as per Fixed income products.`,
    button: "Why not retail investors?",
  },
  {
    content: `Because complexity in understanding and minimum ticket size are the main reasons. Previously, ₹25 Lakhs was the minimum investment to invest in this category.`,
    button: "Got It, Let's Go!",
  },
  {
    content: `You are going to learn about MLDs (Market Linked Debentures)
  
  Why this product is so good, if you understand the concept`,
    button: "Explain MLD in simple words",
  },
  {
    content: `Debt investments are known for protecting capital, while equity/Gold investments aim to grow it. What if there was an investment option that could offer both capital protection and potential growth? Enter Market-Linked Debentures (MLDs)`,
    button: "Explain with a Real Example",
  },
  {
    content: `Ok, let's start with our new product offering which will give you a better idea.
   GoldMCX MLD which is linked with with Gold MCX.
  
  Now I'm going to explain the MLD with Gold MCX as the underlying asset.`,
    button: "Ok",
  },
  {
    content: `Here's the product:
  
  1. Coupon payment linked to the performance of the MCX Gold Index.
  2. Participation Rate of 150% or 1.5x times MCX Gold Index return from 0% to 50% of Gold upside.
  3. Principal Protected at Maturity.
  4. Tenure 36/42 months`,
    button: "Explain in detail",
  },
  {
    content: `Let's say you invested ₹1 Lakh in this MLD. After maturity, there will be 2 scenarios, whether Gold price can go up from today's value or go down.
  
  Let's take an Upside:
  If Gold goes up 35% in this period, then your returns will be 1.5 times of Gold return i.e., 52%`,
    button: "What if Gold price goes down in this period?",
  },
  {
    content: `100% of your principal amount is protected, no -ve returns`,
    button: "One more example",
  },

  {
    content: `Assume: Your friend invested ₹1 Lakh in a Gold ETF or physcial gold.
  You invested ₹1 Lakh in an MLD with Gold MCX as the underlying.
  
  Fast forward 42 month:
  
  Positive case:
  Gold up by 35%, so your friend's return will be approximately: ₹1,35,000 i.e., 35% return
  
  In your case, your returns will be 1.5 times of Gold i.e., your ₹1 Lakh becomes ₹1,52,000`,
    button: "What about the negative case?",
  },
  {
    content: `Negative case:
  Gold down by 20%, your friend's investment value becomes ₹80,000 (20% loss)
  
  In your case, your principal is 100% protected, so your portfolio remains at ₹1,00,000
  
  If Gold is -20% even after 42 months, the market is at a discount. You can redeem this ₹1 Lakh and invest the same amount in Gold, so now you will have more gold quantity than your friend.`,
    button: "Is it too good to believe?",
  },
  {
    content: `I know, let me explain how it works in the backend. There's no magic here.
  
  Hope you know Hybrid mutual funds. Mostly low risk appetite users or senior citizens invest in these hybrid funds because whenever the market crashes, these funds don't fall much. This is because the fund manager invests our money in a combination of Bonds (debt instruments) and Stocks.
  
  When the market crashes, the stock component will go down, but the bond component is not affected, so it will fall less than a pure equity fund.`,
    button: "Yeah, I know",
  },
  {
    content: `Similarly, the MLD is also designed with a combination of Bonds and Options (F&O).
  
  Let's say, if you invest ₹1 Lakh, the MLD manager might design a system like this (just an example):
  
  He will invest ₹80,000 in bonds, so that it's not market-linked.
  The remaining ₹20K will be invested in corresponding index Options as per the return strategy illustrated.
  
  In the GoldMCX down case scenario, if Gold price falls, assume the ₹20K might become zero. But the remaining ₹80K with 9% annualized returns becomes ₹1 Lakh after 3 years, which is equal to your principal amount.
  
  That's it.`,
    button: "Important Learning",
  },
  {
    content: `Many of your friends might be doing F&O daily trading and losing money. But that's not the purpose why F&O exists. Knowledgeable people designed these kinds of products to maximize returns and also protect capital.
  
  Share this with your trader friend`,
    button: "How can I Invest?",
  },
  {
    content: `There are many financial institutions offering MLDs like Edelweiss, InCred, Tata Capital, and Aditya Birla, but they currently cater to HNIs with a minimum investment of ₹10–20 Lakhs.

For every MLD, we are blocking a few units exclusively for Goldbee users with a minimum ticket size of just ₹1 Lakh.

✅ The current bond is offered by InCred Capital.`,
    button: "Wnat to know about Incred",
  },
  {
    content: `InCred is one of the largest financial institutions in India. As of 2024, InCred Capital has over ₹11,400 crore in AUM, serving family offices, HNIs, corporate treasuries, and institutions.`,
    button: "Is it Regulated?",
  },
  {
    content: `Yes, same like Mutual funds, only the authorized SEBI institutions can launch MLDs and they are highly regulated by SEBI.
  
  One needs a demat account to invest in MLDs, and you can track your MLD from your demat account. After the tenure, as per the rules, the amount is automatically credited to your registered bank account.`,
    button: "I want to Invest / I have queries",
  },
];

const formatContent = (content: string) => {
  return content
    .split("\n\n")
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      // Format numbered lists with proper indentation and styling
      paragraph = paragraph.replace(
        /^\d+\.\s+(.*)/gm,
        (match) => `<div class="flex items-start gap-3 mb-2">
          <span class="text-blue-400 font-semibold min-w-[1.25rem] flex-shrink-0">${
            match.match(/^\d+\./)[0]
          }</span>
          <span>${match.replace(/^\d+\.\s+/, "")}</span>
        </div>`
      );

      // Format bullet points with proper indentation and styling
      paragraph = paragraph.replace(
        /^•\s+(.*)/gm,
        (match) => `<div class="flex items-start gap-3 mb-2">
          <span class="text-blue-400 font-semibold min-w-[1.25rem] flex-shrink-0">•</span>
          <span>${match.replace(/^•\s+/, "")}</span>
        </div>`
      );

      return `<div class="mb-4 last:mb-0 leading-relaxed whitespace-pre-wrap text-left">${paragraph}</div>`;
    })
    .join("");
};

export default function GoldMLDChatBot() {
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentStep === 0) {
      addBotMessage(steps[0].content);
    }
  }, [currentStep]);

  const addBotMessage = async (content: string) => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setMessages((prev) => [...prev, { content, type: "bot", id: Date.now() }]);
    setIsTyping(false);
  };

  const handleNext = async () => {
    const userResponse = steps[currentStep].button;
    setMessages((prev) => [
      ...prev,
      { content: userResponse, type: "user", id: Date.now() },
    ]);

    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await addBotMessage(steps[nextStep].content);
    }
  };

  const handleWhatsAppContact = () => {
    window.open("https://wa.me/918801648801", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      {/* Header */}
      <header className="bg-[#2D2D2D] border-b border-gray-700 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-blue-400" />
          <h1 className="text-xl font-bold text-white">MLD's</h1>
        </div>
      </header>

      {/* Chat Container */}
      <main className="flex-1 overflow-hidden flex flex-col max-w-4xl w-full mx-auto">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-6 space-y-6 px-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-6 py-4 ${
                  message.type === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-[#2D2D2D] text-gray-100"
                }`}
              >
                <div
                  className="prose prose-lg prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: formatContent(message.content),
                  }}
                />
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex items-center gap-2 text-gray-400">
              <div className="flex gap-1">
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0s" }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
              <span className="text-sm">Assistant is typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-700 p-4 sticky bottom-0 bg-[#1A1A1A]">
          <div className="max-w-4xl mx-auto flex gap-4">
            <button
              onClick={handleNext}
              disabled={currentStep >= steps.length - 1 && !isTyping}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep < steps.length
                ? steps[currentStep].button
                : "Chat Completed"}
            </button>
            {currentStep >= steps.length - 1 && (
              <button
                onClick={handleWhatsAppContact}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                Contact on WhatsApp
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
